// src/private/src/pages/app/clients/ClientSchedulePage.jsx
//
// Client-facing meeting booking page. Tier-driven quota, two-step AI
// deflection gate, calendar slot picker, and a request form for
// Starter / over-quota clients.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';

import {
  cancelBooking,
  createBooking,
  createMeetingRequest,
  fetchAvailability,
  fetchBookings,
  fetchMeetingHistory,
  fetchMeetingPolicy,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { useMessage } from '@src/context/common';


// "Quick action" chips on the AI gate card. Each one deep-links into
// /agent-chat with a pre-filled prompt — these are intentionally the
// topics clients most often want a meeting for but that the agent can
// actually answer over chat. Tweak freely.
const AGENT_QUICK_ACTIONS = [
  {
    label: 'Update ZIP codes',
    prompt: 'I need to update my ZIP code coverage. Can you help me?',
  },
  {
    label: 'Update budget',
    prompt: 'I want to change my monthly budget. What are my options?',
  },
  {
    label: 'How are we pacing?',
    prompt: 'How are we pacing this month? Show me spend vs. plan.',
  },
  {
    label: 'Recent lead quality',
    prompt: 'Walk me through the quality of leads from the last 7 days.',
  },
];


// Browser-resolved IANA timezone (e.g. "America/New_York"). Falls back
// quietly if the browser doesn't expose Intl (very old environments).
function resolveBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}


function ClientSchedulePage() {
  const { clientSlug } = useParams();
  const message = useMessage();

  // Data
  const [policy, setPolicy] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [meetingHistory, setMeetingHistory] = useState([]);

  // Async state
  const [loading, setLoading] = useState({
    policy: true,
    slots: false,
    bookings: true,
    history: true,
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // UI state
  const [calendarRevealed, setCalendarRevealed] = useState(false);
  const [gateDismissed, setGateDismissed] = useState(false); // "Yes, that answered me"
  const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
  const [pendingSlot, setPendingSlot] = useState(null); // ISO string
  const [bookingNotes, setBookingNotes] = useState('');
  const [guestEmails, setGuestEmails] = useState(''); // comma-separated
  const [rescheduling, setRescheduling] = useState(false); // post-cancel flow

  // Request-form state (Starter / over quota)
  const [requestTopic, setRequestTopic] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [requestWindow, setRequestWindow] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  const pickerAnchorRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────
  // Loaders
  // ────────────────────────────────────────────────────────────────────────

  const loadPolicy = useCallback(async () => {
    setLoading((s) => ({ ...s, policy: true }));
    try {
      const res = await fetchMeetingPolicy(clientSlug);
      setPolicy(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load meeting policy.');
    } finally {
      setLoading((s) => ({ ...s, policy: false }));
    }
  }, [clientSlug]);

  const loadBookings = useCallback(async () => {
    setLoading((s) => ({ ...s, bookings: true }));
    try {
      const res = await fetchBookings(clientSlug);
      setBookings(res.data || []);
    } catch (err) {
      // Soft-fail — page is still useful without history.
    } finally {
      setLoading((s) => ({ ...s, bookings: false }));
    }
  }, [clientSlug]);

  const loadMeetingHistory = useCallback(async () => {
    setLoading((s) => ({ ...s, history: true }));
    try {
      const res = await fetchMeetingHistory(clientSlug, { limit: 10 });
      setMeetingHistory(res.data?.meetings || []);
    } catch (err) {
      // Soft-fail — page is useful even if Zoom webhook isn't wired yet.
    } finally {
      setLoading((s) => ({ ...s, history: false }));
    }
  }, [clientSlug]);

  const loadSlots = useCallback(async () => {
    setLoading((s) => ({ ...s, slots: true }));
    try {
      const res = await fetchAvailability(clientSlug);
      const list = (res.data || []).map((s) => s.starts_at);
      setSlots(list);
      if (list.length > 0 && !selectedDay) {
        setSelectedDay(dayjs(list[0]).format('YYYY-MM-DD'));
      }
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not load availability.',
      );
    } finally {
      setLoading((s) => ({ ...s, slots: false }));
    }
  }, [clientSlug, selectedDay, message]);

  useEffect(() => {
    loadPolicy();
    loadBookings();
    loadMeetingHistory();
  }, [loadPolicy, loadBookings, loadMeetingHistory]);

  // Lazy-load availability: only when the client passes the AI gate, or
  // immediately if the calendar is the only thing on the page.
  useEffect(() => {
    if (calendarRevealed) loadSlots();
  }, [calendarRevealed, loadSlots]);

  // ────────────────────────────────────────────────────────────────────────
  // Derived state
  // ────────────────────────────────────────────────────────────────────────

  const canSelfServe =
    policy?.allow_self_serve_booking && policy?.quota_remaining > 0;
  const showRequestForm =
    policy && !canSelfServe && policy?.allow_request_form;

  // Group slots by local YYYY-MM-DD so the day selector and time selector
  // can render quickly.
  const slotsByDay = useMemo(() => {
    const map = {};
    for (const iso of slots) {
      const key = dayjs(iso).format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(iso);
    }
    return map;
  }, [slots]);

  const availableDays = useMemo(() => Object.keys(slotsByDay), [slotsByDay]);

  // ────────────────────────────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────────────────────────────

  const handleBookingConfirm = async () => {
    if (!pendingSlot) return;
    // Split the comma-separated guest field into a clean email list.
    // Filter to anything that looks roughly email-shaped — final
    // validation happens server-side via DRF's EmailField.
    const parsedGuests = guestEmails
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

    setSubmitting(true);
    try {
      await createBooking(clientSlug, {
        starts_at: pendingSlot,
        client_notes: bookingNotes,
        attendee_emails: parsedGuests,
      });
      message.success(
        'Meeting booked. Check your email for the invite — or use "Add to calendar" on the booking below.',
      );
      setPendingSlot(null);
      setBookingNotes('');
      setGuestEmails('');
      setRescheduling(false);
      // Refresh everything so quota + history + slot list all update.
      await Promise.all([loadPolicy(), loadBookings(), loadSlots()]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Could not book that slot.';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (booking) => {
    if (!window.confirm('Cancel this meeting?')) return;
    try {
      const res = await cancelBooking(booking.id, { reason: '' });
      message.success(
        res.data?.quota_was_restored
          ? 'Meeting cancelled — your quota was restored.'
          : 'Meeting cancelled.',
      );
      await Promise.all([loadPolicy(), loadBookings()]);
      if (calendarRevealed) loadSlots();
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not cancel meeting.',
      );
    }
  };

  // Reschedule = cancel then re-open the picker. The backend restores
  // quota if the booking was >cancel_restores_quota_within_hours away,
  // so most reschedules are quota-neutral. We surface the result via
  // the cancel toast.
  const handleReschedule = async (booking) => {
    if (
      !window.confirm(
        'Cancel this meeting and pick a new time? If you\'re cancelling within 24h of the meeting your monthly quota may not be restored.',
      )
    ) {
      return;
    }
    try {
      const res = await cancelBooking(booking.id, { reason: 'Rescheduling' });
      message.success(
        res.data?.quota_was_restored
          ? 'Cancelled. Pick a new time below.'
          : 'Cancelled (quota consumed — this counts against this month). Pick a new time below.',
      );
      setCalendarRevealed(true);
      setRescheduling(true);
      await Promise.all([loadPolicy(), loadBookings(), loadSlots()]);
      // Scroll to the picker so the next step is obvious.
      setTimeout(() => {
        pickerAnchorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not start reschedule.',
      );
    }
  };

  const handleRequestSubmit = async () => {
    if (!requestTopic.trim()) {
      message.error('Please enter a topic.');
      return;
    }
    setSubmitting(true);
    try {
      await createMeetingRequest(clientSlug, {
        topic: requestTopic.trim(),
        body: requestBody.trim(),
        requested_window: requestWindow.trim(),
      });
      setRequestSubmitted(true);
      setRequestTopic('');
      setRequestBody('');
      setRequestWindow('');
      message.success('Request submitted — we\'ll follow up by email.');
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not submit request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────

  if (loading.policy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!policy) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No meeting policy is configured for this client's tier.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Schedule a Meeting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Office hours: Tuesdays & Wednesdays, 1:00&nbsp;–&nbsp;4:00&nbsp;PM ET
      </Typography>

      <QuotaStrip policy={policy} />

      {canSelfServe && !calendarRevealed && !gateDismissed && (
        <AIGateCard
          clientSlug={clientSlug}
          onContinue={() => setCalendarRevealed(true)}
          onDismiss={() => setGateDismissed(true)}
        />
      )}

      {canSelfServe && !calendarRevealed && gateDismissed && (
        <GateThankYouCard onReopen={() => setGateDismissed(false)} />
      )}

      {canSelfServe && calendarRevealed && (
        <Box ref={pickerAnchorRef}>
          {rescheduling && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Rescheduling — pick a new time below.
            </Alert>
          )}
          <BookingPicker
            loading={loading.slots}
            policy={policy}
            slotsByDay={slotsByDay}
            availableDays={availableDays}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            onSelectSlot={(iso) => setPendingSlot(iso)}
          />
        </Box>
      )}

      {showRequestForm && (
        <RequestForm
          policy={policy}
          topic={requestTopic}
          body={requestBody}
          window={requestWindow}
          submitted={requestSubmitted}
          submitting={submitting}
          onTopicChange={setRequestTopic}
          onBodyChange={setRequestBody}
          onWindowChange={setRequestWindow}
          onSubmit={handleRequestSubmit}
          onReset={() => setRequestSubmitted(false)}
        />
      )}

      <UpcomingBookings
        bookings={bookings}
        loading={loading.bookings}
        onCancel={handleCancel}
        onReschedule={handleReschedule}
      />

      <PastMeetingsTimeline
        bookings={bookings}
        meetings={meetingHistory}
        loading={loading.bookings || loading.history}
      />

      <CancelledMeetingsAccordion
        bookings={bookings}
        loading={loading.bookings}
      />

      <ConfirmDialog
        open={Boolean(pendingSlot)}
        slot={pendingSlot}
        policy={policy}
        notes={bookingNotes}
        guests={guestEmails}
        submitting={submitting}
        onNotesChange={setBookingNotes}
        onGuestsChange={setGuestEmails}
        onCancel={() => {
          setPendingSlot(null);
          setBookingNotes('');
          setGuestEmails('');
        }}
        onConfirm={handleBookingConfirm}
      />
    </Box>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

function QuotaStrip({ policy }) {
  const pct = policy.meetings_per_month
    ? Math.min(100, (policy.quota_used / policy.meetings_per_month) * 100)
    : 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');
  const lateCancelCount = policy.quota_late_cancellations || 0;
  const cancelWindowHours = policy.cancel_restores_quota_within_hours || 24;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              {policy.tier_name} tier
            </Typography>
            <Typography variant="h6">
              {policy.quota_used} of {policy.meetings_per_month} meetings used this month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {policy.meetings_per_month > 0
                ? `${policy.quota_remaining} remaining · ${policy.duration_minutes} min each · resets ${resets}`
                : 'Your tier does not include scheduled meetings — submit a request below.'}
            </Typography>
            {lateCancelCount > 0 && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Includes {lateCancelCount} cancelled within {cancelWindowHours}h
                </Typography>
                <Tooltip
                  title={`Cancellations more than ${cancelWindowHours} hours before the meeting restore your quota. Cancellations inside that window still count toward your monthly total.`}
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </Stack>
            )}
          </Box>
          {policy.meetings_per_month > 0 && (
            <Box sx={{ width: { xs: '100%', sm: 240 } }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={policy.quota_remaining > 0 ? 'primary' : 'warning'}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}


function AIGateCard({ clientSlug, onContinue, onDismiss }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [freeText, setFreeText] = useState('');

  // Build the deep-link URL to /agent-chat. `client=<slug>` pre-selects
  // the right client and `autosend=1` makes the agent fire the prompt
  // immediately instead of just pre-filling the input.
  const askAgent = (prompt) => {
    const trimmed = (prompt || '').trim();
    if (!trimmed) return;
    const params = new URLSearchParams({
      prompt: trimmed,
      autosend: '1',
    });
    if (clientSlug) params.set('client', clientSlug);
    navigate(`/agent-chat?${params.toString()}`);
  };

  const handleFreeSubmit = (e) => {
    e?.preventDefault?.();
    askAgent(freeText);
  };

  return (
    <Card
      sx={{
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.2),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6">Have a quick question?</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ask anything below — your AI agent has live access to your account
          and answers in seconds.
        </Typography>

        <Box component="form" onSubmit={handleFreeSubmit} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask a question…"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            InputProps={{
              endAdornment: (
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={!freeText.trim()}
                  endIcon={<SendIcon />}
                  sx={{ ml: 1, whiteSpace: 'nowrap' }}
                >
                  Ask
                </Button>
              ),
            }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Or try one of these
        </Typography>
        <Stack spacing={1} sx={{ mb: 3 }}>
          {AGENT_QUICK_ACTIONS.map((action) => (
            <Button
              key={action.label}
              variant="outlined"
              color="primary"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => askAgent(action.prompt)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.25,
                px: 2,
                borderRadius: 999,
                fontWeight: 400,
                color: 'text.primary',
                borderColor: alpha(theme.palette.primary.main, 0.25),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
          Still need a 1:1?
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onDismiss}
            sx={{ flex: { sm: 1 } }}
          >
            No — thanks, all set
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<EventAvailableIcon />}
            onClick={onContinue}
            sx={{ flex: { sm: 1 } }}
          >
            Yes — show me available times
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}


function GateThankYouCard({ onReopen }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body1">
            Glad that helped! You can come back any time.
          </Typography>
          <Button size="small" onClick={onReopen}>
            Actually, I do need a meeting
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}


function BookingPicker({
  loading,
  policy,
  slotsByDay,
  availableDays,
  selectedDay,
  onSelectDay,
  onSelectSlot,
}) {
  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading available slots…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (availableDays.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="info">
            No open slots in the next {policy.max_horizon_days} days. Try again
            later, or submit a request below if you need a specific time.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const slotsForSelected = selectedDay ? slotsByDay[selectedDay] || [] : [];
  const browserTz = resolveBrowserTimezone();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pick a time
        </Typography>

        <Typography variant="overline" color="text.secondary">
          Day
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {availableDays.map((day) => {
            const d = dayjs(day);
            return (
              <Chip
                key={day}
                label={`${d.format('ddd')} · ${d.format('MMM D')}`}
                onClick={() => onSelectDay(day)}
                color={selectedDay === day ? 'primary' : 'default'}
                variant={selectedDay === day ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="overline" color="text.secondary">
            Time
          </Typography>
          {browserTz && (
            <Typography variant="caption" color="text.secondary">
              Times shown in your timezone: {browserTz}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {slotsForSelected.map((iso) => (
            <Chip
              key={iso}
              label={dayjs(iso).format('h:mm A')}
              onClick={() => onSelectSlot(iso)}
              clickable
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


// Per-tier "what to expect" copy in the confirm dialog. Falls back to
// the generic line if the tier name doesn't match the known set.
function tierExpectationCopy(tierName) {
  const name = (tierName || '').toLowerCase();
  if (name.includes('elite')) {
    return 'Your account strategist will join with the latest performance data. Add anyone else from your team who should be there.';
  }
  if (name.includes('growth')) {
    return 'Your account lead will walk through your results and answer your questions. Add anyone else from your team who should attend.';
  }
  if (name.includes('scaling')) {
    return 'A focused 15-minute working session — best for one specific question. Use the notes field below so we come in prepared.';
  }
  return 'You\'ll get an email invite with the Zoom join link. Add anyone else who should attend below — they\'ll be invited too.';
}


function ConfirmDialog({
  open,
  slot,
  policy,
  notes,
  guests,
  submitting,
  onNotesChange,
  onGuestsChange,
  onCancel,
  onConfirm,
}) {
  if (!slot) return null;
  const start = dayjs(slot);
  const end = start.add(policy.duration_minutes, 'minute');
  const expectationCopy = tierExpectationCopy(policy.tier_name);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm meeting</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              When
            </Typography>
            <Typography variant="body1">
              {start.format('dddd, MMMM D')}
            </Typography>
            <Typography variant="body1">
              {start.format('h:mm A')} – {end.format('h:mm A')} ({policy.duration_minutes} min)
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              What you can expect
            </Typography>
            <Typography variant="body2">{expectationCopy}</Typography>
          </Box>
          <TextField
            label="Add guests (optional)"
            value={guests}
            onChange={(e) => onGuestsChange(e.target.value)}
            fullWidth
            placeholder="email1@company.com, email2@company.com"
            helperText="Comma-separated email addresses. They'll get the same invite."
          />
          <TextField
            label="What would you like to discuss? (optional)"
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            fullWidth
            placeholder="A short note helps us prep — e.g. 'Wondering about creative refresh strategy.'"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <EventAvailableIcon />}
        >
          Book meeting
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function RequestForm({
  policy,
  topic,
  body,
  window: windowText,
  submitted,
  submitting,
  onTopicChange,
  onBodyChange,
  onWindowChange,
  onSubmit,
  onReset,
}) {
  const isOverQuota =
    policy.allow_self_serve_booking && policy.quota_remaining === 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Request a meeting
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isOverQuota
            ? `You've used your quota for this month — it resets on ${resets}. In the meantime, tell us what you need and we'll follow up by email.`
            : `Your tier doesn't include scheduled office-hours meetings. Submit a request and we'll follow up by email to find a time that works.`}
        </Typography>

        {submitted ? (
          <Alert severity="success" action={
            <Button color="inherit" size="small" onClick={onReset}>
              Submit another
            </Button>
          }>
            Request submitted. We'll follow up by email shortly.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="What do you want to discuss?"
              value={topic}
              onChange={(e) => onTopicChange(e.target.value)}
              required
              fullWidth
              placeholder="e.g. Creative refresh strategy for Q3"
            />
            <TextField
              label="Details (optional)"
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Context that helps us prep ahead of time."
            />
            <TextField
              label="When would work for you? (optional)"
              value={windowText}
              onChange={(e) => onWindowChange(e.target.value)}
              fullWidth
              placeholder="e.g. Thursday afternoon, next week"
            />
            <Box>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={submitting || !topic.trim()}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                Submit request
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}


function UpcomingBookings({ bookings, loading, onCancel, onReschedule }) {
  if (loading) return null;
  const now = dayjs();
  const upcoming = (bookings || []).filter(
    (b) => b.status === 'active' && dayjs(b.starts_at).isAfter(now),
  );
  if (upcoming.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upcoming meetings
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {upcoming.map((b) => (
            <BookingRow
              key={b.id}
              booking={b}
              onCancel={onCancel}
              onReschedule={onReschedule}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


// Joins completed bookings + Zoom meeting notes by booking_id into a
// single time-sorted timeline so the client sees "Tuesday call → here's
// what we discussed" as one row instead of two. Cancellations are
// shown separately in `CancelledMeetingsAccordion` so they don't muddy
// the record of meetings that actually happened.
function PastMeetingsTimeline({ bookings, meetings, loading }) {
  const rows = useMemo(() => {
    const now = dayjs();
    const notesByBookingId = new Map();
    const unmatchedNotes = [];
    for (const note of meetings || []) {
      if (note.booking_id) {
        notesByBookingId.set(note.booking_id, note);
      } else {
        unmatchedNotes.push(note);
      }
    }

    // Completed = explicitly flipped to COMPLETED by the Zoom webhook,
    // OR a still-ACTIVE booking whose start time has passed (webhook
    // hasn't fired yet but the meeting demonstrably happened).
    const pastBookings = (bookings || []).filter(
      (b) =>
        b.status === 'completed' ||
        (b.status === 'active' && dayjs(b.starts_at).isBefore(now)),
    );

    const merged = pastBookings.map((b) => ({
      kind: 'booking',
      key: `booking-${b.id}`,
      sortDate: b.starts_at,
      booking: b,
      note: notesByBookingId.get(b.id) || null,
    }));

    for (const note of unmatchedNotes) {
      merged.push({
        kind: 'note',
        key: `note-${note.id}`,
        sortDate: note.started_at,
        booking: null,
        note,
      });
    }

    merged.sort((a, b) => {
      const ad = a.sortDate ? new Date(a.sortDate).getTime() : 0;
      const bd = b.sortDate ? new Date(b.sortDate).getTime() : 0;
      return bd - ad;
    });
    return merged.slice(0, 10);
  }, [bookings, meetings]);

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Past meetings
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Summaries auto-generated by Zoom AI Companion. The AI agent uses them as context when answering your questions.
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {rows.map((row) => (
            <PastMeetingRow key={row.key} row={row} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


function PastMeetingRow({ row }) {
  const { booking, note } = row;
  const when = row.sortDate ? dayjs(row.sortDate) : null;
  const title = note?.title || (booking ? `${booking.duration_minutes}-min meeting` : 'Meeting');
  const actionItems = Array.isArray(note?.action_items) ? note.action_items : [];

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
      >
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {when ? when.format('MMM D, h:mm A') : '—'}
          {note?.duration_minutes
            ? ` · ${note.duration_minutes} min`
            : booking?.duration_minutes
              ? ` · ${booking.duration_minutes} min`
              : ''}
        </Typography>
      </Stack>
      {booking && !note && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          No AI summary captured for this call.
        </Typography>
      )}
      {note?.summary && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
        >
          {note.summary}
        </Typography>
      )}
      {actionItems.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Action items:
          </Typography>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {actionItems.slice(0, 5).map((item, idx) => {
              const text =
                typeof item === 'string' ? item : item?.text || JSON.stringify(item);
              return (
                <li key={idx}>
                  <Typography variant="body2" component="span">
                    {text}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </Box>
      )}
      {note?.transcript_url && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            href={note.transcript_url}
            target="_blank"
            rel="noreferrer"
          >
            View transcript
          </Button>
        </Box>
      )}
    </Box>
  );
}


// Collapsible section listing cancelled bookings. Quota-consumed
// cancellations are flagged so the client can see exactly which
// late-cancel ate their quota when the quota strip says "Includes N
// cancelled within 24h".
function CancelledMeetingsAccordion({ bookings, loading }) {
  const cancelled = useMemo(() => {
    return (bookings || [])
      .filter(
        (b) =>
          b.status === 'cancelled_restored' || b.status === 'cancelled_consumed',
      )
      .sort((a, b) => {
        const ad = a.starts_at ? new Date(a.starts_at).getTime() : 0;
        const bd = b.starts_at ? new Date(b.starts_at).getTime() : 0;
        return bd - ad;
      })
      .slice(0, 15);
  }, [bookings]);

  if (loading) return null;
  if (cancelled.length === 0) return null;

  const consumedCount = cancelled.filter(
    (b) => b.status === 'cancelled_consumed',
  ).length;

  return (
    <Accordion sx={{ mb: 3 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          Cancelled meetings ({cancelled.length}
          {consumedCount > 0 ? ` · ${consumedCount} counted toward quota` : ''})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack divider={<Divider flexItem />} spacing={1.5}>
          {cancelled.map((b) => {
            const when = dayjs(b.starts_at);
            const consumed = b.status === 'cancelled_consumed';
            return (
              <Stack
                key={b.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'baseline' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2">
                    {when.format('MMM D, h:mm A')} · {b.duration_minutes} min
                  </Typography>
                  {b.cancellation_reason && (
                    <Typography variant="caption" color="text.secondary">
                      {b.cancellation_reason}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={consumed ? 'Counted toward quota' : 'Quota restored'}
                  color={consumed ? 'warning' : 'default'}
                  variant={consumed ? 'filled' : 'outlined'}
                />
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}


function BookingRow({ booking, onCancel, onReschedule }) {
  const start = dayjs(booking.starts_at);
  const joinHref = booking.zoom_join_url || booking.gcal_meet_link;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography variant="body1">
          {start.format('dddd, MMM D · h:mm A')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {booking.duration_minutes} min · {booking.status_display}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {joinHref && (
          <Button
            size="small"
            startIcon={<VideoCallIcon />}
            href={joinHref}
            target="_blank"
            rel="noreferrer"
          >
            Join
          </Button>
        )}
        {booking.gcal_html_link && (
          <Button
            size="small"
            startIcon={<CalendarMonthIcon />}
            href={booking.gcal_html_link}
            target="_blank"
            rel="noreferrer"
          >
            Add to calendar
          </Button>
        )}
        {onReschedule && (
          <Button
            size="small"
            startIcon={<EventRepeatIcon />}
            onClick={() => onReschedule(booking)}
          >
            Reschedule
          </Button>
        )}
        {onCancel && (
          <Button
            size="small"
            color="warning"
            onClick={() => onCancel(booking)}
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
}


export default ClientSchedulePage;
